import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { CompareFacade } from 'src/app/extensions/compare/facades/compare.facade';
import { OrderTemplatesFacade } from 'src/app/extensions/order-templates/facades/order-templates.facade';
import { OrderTemplate } from 'src/app/extensions/order-templates/models/order-template/order-template.model';
import { WishlistsFacade } from 'src/app/extensions/wishlists/facades/wishlists.facade';
import { Wishlist } from 'src/app/extensions/wishlists/models/wishlist/wishlist.model';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { FilterNavigation } from 'ish-core/models/filter-navigation/filter-navigation.model';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { FeatureToggleService } from 'ish-core/utils/feature-toggle/feature-toggle.service';
import { URLFormParams } from 'ish-core/utils/url-form-params';
import { ErrorMessageComponent } from 'ish-shared/components/common/error-message/error-message.component';
import { LoadingComponent } from 'ish-shared/components/common/loading/loading.component';
import { FilterCheckboxComponent } from 'ish-shared/components/filter/filter-checkbox/filter-checkbox.component';
import { FilterCollapsibleComponent } from 'ish-shared/components/filter/filter-collapsible/filter-collapsible.component';
import { FilterCompareComponent } from 'ish-shared/components/filter/filter-compare/filter-compare.component';
import { FilterDiasCollapsibleComponent } from 'ish-shared/components/filter/filter-dias-collapsible/filter-dias-collapsible.component';
import { FilterDropdownComponent } from 'ish-shared/components/filter/filter-dropdown/filter-dropdown.component';
import { FilterSwatchImagesComponent } from 'ish-shared/components/filter/filter-swatch-images/filter-swatch-images.component';
import { FilterTextComponent } from 'ish-shared/components/filter/filter-text/filter-text.component';
import { CustomFilterWishlistComponent } from 'ish-shared/dias/components/filter/custom-filter-wishlist/custom-filter-wishlist.component';
import { FilterOrderTemplateComponent } from 'ish-shared/dias/components/filter/filter-order-template/filter-order-template.component';

@Component({
  selector: 'ish-filter-navigation-sidebar',
  templateUrl: './filter-navigation-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FilterCollapsibleComponent,
    FilterTextComponent,
    FilterCheckboxComponent,
    FilterSwatchImagesComponent,
    FilterDropdownComponent,
    FilterDiasCollapsibleComponent,
    ErrorMessageComponent,
    LoadingComponent,
    FilterOrderTemplateComponent,
    FilterDiasCollapsibleComponent,
    CustomFilterWishlistComponent,
    FilterCompareComponent,
    TranslateModule,
  ],
})
export class FilterNavigationSidebarComponent implements OnInit {
  private orderTemplatesFacade = inject(OrderTemplatesFacade);
  private compareFacade = inject(CompareFacade);
  private accountFacade = inject(AccountFacade);
  private wishList = inject(WishlistsFacade);
  private featureToggleService = inject(FeatureToggleService);
  private wishlistsFacade = inject(WishlistsFacade);

  readonly filterNavigation = input.required<FilterNavigation>();
  readonly applyFilter = output<{
    searchParameter: URLFormParams;
  }>();
  /**
   * The list of wishlists of the customer.
   */
  wishlists$: Observable<Wishlist[]>;
  /**
   * The list of order templates of the customer.
   */
  orderTemplates$: Observable<OrderTemplate[]>;
  /**
   * Indicator for loading state of order templates
   */
  orderTemplateLoading$: Observable<boolean>;
  /**
   * Error state in case of an error during creation of a new order template.
   */
  orderTemplateError$: Observable<HttpError>;
  isWishlist$: Observable<boolean>;
  isOrderTemplate$: Observable<boolean>;
  wishlistLoading$: Observable<boolean>;
  wishlistError$: Observable<HttpError>;

  compareProducts$: Observable<string[]>;
  isLoggedIn$: Observable<boolean>;

  ngOnInit() {
    this.orderTemplates$ = this.orderTemplatesFacade.currentUsersOrderTemplates$();
    this.orderTemplateLoading$ = this.orderTemplatesFacade.orderTemplateLoading$;
    this.wishlists$ = this.wishList.wishlists$;
    this.orderTemplateError$ = this.orderTemplatesFacade.orderTemplateError$;
    this.compareProducts$ = this.compareFacade.compareProducts$;
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;
    this.isWishlist$ = this.featureToggleService.enabled$('wishlists');
    this.isOrderTemplate$ = this.featureToggleService.enabled$('orderTemplates');
    this.wishlistLoading$ = this.wishlistsFacade.wishlistLoading$;
    this.wishlistError$ = this.wishlistsFacade.wishlistError$;
  }
  /**
   * keeps the collapsed state of sub components when changing filters
   */
  collapsedElements: { [id: string]: boolean } = {};

  /**
   * keeps the show all state of sub components when changing filters
   */
  showAllElements: { [id: string]: boolean } = {};
}
